import ApiService from '@/utils/ApiService';

/**
 * Article-content generation via the tenant's connected ("bring your own") LLM.
 *
 * Shared by the Quick Edit Bar (``NodeEditContent``) and the content form
 * (``ContentItem``) so the KAIA toolbar button behaves identically wherever a
 * point's content is edited. Deliberately Vuex-free — the caller supplies the
 * map id from the store — so it unit-tests against a mocked ApiService alone.
 */
export default function useContentLlm() {
    /**
     * Generate content HTML for a point.
     *
     * :param mapId: ID of the map the point belongs to.
     * :param nodeId: ID of the point being generated. When given, the backend
     *     resolves the point's ancestor titles (anchor → parent) from the map
     *     hierarchy and narrows the prompt to that context. Omitted when falsy.
     * :param title: The point's title, used as the generation prompt; an empty
     *     string is sent when none is given.
     * :param language: ISO id of the language to generate in.
     * :param kind: Which field to generate — ``'article'`` (default, the main
     *     body), ``'key_facts'`` (a supplementary bullet list) or
     *     ``'references'`` (a further-reading list). Omitted from the payload
     *     when falsy so the backend applies its ``'article'`` default.
     * :param context: Grounding text for the marginal fields — typically the
     *     already-written article — so they complement rather than repeat it.
     *     Omitted when falsy.
     * :returns: HTML fragment to insert at the cursor.
     * :rtype: Promise<string>
     */
    async function generateNodeContent({ mapId, nodeId, title, language, kind, context }) {
        const payload = { title: title || '', language };
        if (nodeId != null) payload.node_id = nodeId;
        if (kind) payload.kind = kind;
        if (context) payload.context = context;
        const { data } = await ApiService.post(`/maps/${mapId}/llm/content`, payload);
        return data.content;
    }

    return { generateNodeContent };
}
